import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Plug, PlugZap, ChevronDown, ChevronRight, Download, Sparkles, AlertTriangle, CheckCircle2, Copy, Power } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cn = (...c) => c.filter(Boolean).join(" ");

function Badge({ children, tone = "muted" }) {
  const tones = {
    ok: "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30",
    bad: "bg-red-500/15 text-red-300 ring-1 ring-red-500/30",
    muted: "bg-zinc-800 text-zinc-300 ring-1 ring-zinc-700",
    info: "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium", tones[tone])}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = "primary", disabled, testid, className }) {
  const variants = {
    primary: "bg-white text-zinc-900 hover:bg-zinc-200",
    ghost: "bg-transparent text-zinc-200 hover:bg-zinc-800 ring-1 ring-zinc-700",
    danger: "bg-red-500/10 text-red-300 hover:bg-red-500/20 ring-1 ring-red-500/30",
    accent: "bg-amber-400 text-zinc-900 hover:bg-amber-300",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}

function CodeBlock({ children }) {
  return (
    <pre className="overflow-x-auto rounded-md bg-zinc-950 p-3 text-xs text-zinc-300 ring-1 ring-zinc-800">
      {children}
    </pre>
  );
}

function TikTokCaptionHelper({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-2 space-y-2 border-t border-zinc-800 pt-2" data-testid={testid}>
      <p className="text-[11px] text-zinc-500">
        Open TikTok → Inbox → tap the new notification → paste this caption.
      </p>
      <textarea readOnly value={text || ""} rows={4}
        className="w-full resize-none rounded bg-black/40 px-2 py-1.5 text-[11px] text-zinc-200 ring-1 ring-zinc-800 focus:outline-none" />
      <button
        onClick={copy}
        data-testid={`${testid}-copy-btn`}
        className="inline-flex items-center gap-1.5 rounded bg-zinc-800 px-2 py-1 text-[11px] text-zinc-100 hover:bg-zinc-700"
      >
        <Copy className="h-3 w-3" /> {copied ? "Copied!" : "Copy caption to clipboard"}
      </button>
    </div>
  );
}

export default function PocPage() {
  const [status, setStatus] = useState({ loading: true });
  const [connecting, setConnecting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [clipsByProject, setClipsByProject] = useState({});
  const [selectedClip, setSelectedClip] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [callbackBanner, setCallbackBanner] = useState(null);

  // Read ?connected=restream or ?error=... from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "restream") {
      setCallbackBanner({ kind: "ok", msg: "Restream connected successfully." });
      window.history.replaceState({}, document.title, "/poc");
    } else if (params.get("error")) {
      setCallbackBanner({ kind: "bad", msg: `OAuth error: ${params.get("error")}` });
      window.history.replaceState({}, document.title, "/poc");
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/restream/status`);
      setStatus({ loading: false, ...r.data });
    } catch (e) {
      setStatus({ loading: false, connected: false, error: e?.message });
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const r = await axios.get(`${API}/restream/projects`);
      setProjects(r.data.projects || []);
    } catch (e) {
      setProjectsError(e?.response?.data?.detail || e.message);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (status.connected) fetchProjects();
  }, [status.connected, fetchProjects]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/auth/restream/login`);
      window.location.href = r.data.url;
    } catch (e) {
      setCallbackBanner({
        kind: "bad",
        msg: e?.response?.data?.detail || "Failed to start OAuth. Check /app/memory/SETUP_RESTREAM.md.",
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    await axios.post(`${API}/auth/restream/disconnect`);
    setStatus({ loading: false, connected: false });
    setProjects([]);
    setExpanded({});
    setClipsByProject({});
    setSelectedClip(null);
    setDownloadUrl(null);
    setAiResult(null);
  };

  const toggleProject = async (projectId) => {
    const isOpen = !!expanded[projectId];
    setExpanded((prev) => ({ ...prev, [projectId]: !isOpen }));
    if (!isOpen && !clipsByProject[projectId]) {
      try {
        const r = await axios.get(`${API}/restream/projects/${encodeURIComponent(projectId)}/clips`);
        setClipsByProject((prev) => ({ ...prev, [projectId]: r.data.clips || [] }));
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const errObj =
          typeof detail === "object" && detail !== null
            ? {
                message: detail.message || "Restream couldn't load this project's clips.",
                upstream_status: detail.upstream_status,
                upstream_url: detail.upstream_url,
                upstream_body: detail.upstream_body,
              }
            : { message: typeof detail === "string" ? detail : e.message };
        setClipsByProject((prev) => ({ ...prev, [projectId]: { error: errObj } }));
      }
    }
  };

  const selectClip = (clip, project) => {
    setSelectedClip({ ...clip, projectId: project.id, projectTitle: project.title, projectCreatedAt: project.createdAt });
    setDownloadUrl(null);
    setAiResult(null);
    setAiError(null);
  };

  const getDownloadUrl = async () => {
    if (!selectedClip) return;
    setDownloadLoading(true);
    try {
      const r = await axios.post(`${API}/restream/clips/${selectedClip.id}/download-url`);
      setDownloadUrl(r.data.downloadUrl);
    } catch (e) {
      setDownloadUrl(null);
      setCallbackBanner({ kind: "bad", msg: e?.response?.data?.detail || "Failed to fetch download URL." });
    } finally {
      setDownloadLoading(false);
    }
  };

  const generateHashtags = async () => {
    if (!selectedClip) return;
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      const r = await axios.post(`${API}/ai/generate-hashtags`, {
        title: selectedClip.title || "",
        transcript: selectedClip.transcript || "",
        projectTitle: selectedClip.projectTitle || "",
        projectCreatedAt: selectedClip.projectCreatedAt || "",
      });
      setAiResult(r.data);
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const connected = status.connected === true;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="poc-page">
      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* Header */}
        <header className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="mb-1 text-xs uppercase tracking-[0.2em] text-zinc-500">Phase 0 · POC</p>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              True Believer Fam <span className="text-amber-400">Auto-Clipper</span>
            </h1>
            <p className="mt-2 max-w-xl text-sm text-zinc-400">
              Connect Restream, browse your clip projects, pull a temporary download URL, and generate
              faith-aware hashtags + a short pastoral caption with AI.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="status-area">
            {status.loading ? (
              <Badge tone="muted">
                <Loader2 className="h-3 w-3 animate-spin" /> Checking…
              </Badge>
            ) : connected ? (
              <Badge tone="ok">
                <CheckCircle2 className="h-3.5 w-3.5" /> Connected
              </Badge>
            ) : (
              <Badge tone="bad">
                <AlertTriangle className="h-3.5 w-3.5" /> Not connected
              </Badge>
            )}
            {connected && (
              <Button variant="ghost" onClick={handleDisconnect} testid="disconnect-btn">
                <Power className="h-4 w-4" /> Disconnect
              </Button>
            )}
          </div>
        </header>

        {/* Callback banner */}
        {callbackBanner && (
          <div
            data-testid="callback-banner"
            className={cn(
              "mb-6 rounded-md px-4 py-3 text-sm ring-1",
              callbackBanner.kind === "ok"
                ? "bg-emerald-500/10 text-emerald-200 ring-emerald-500/30"
                : "bg-red-500/10 text-red-200 ring-red-500/30",
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <span>{callbackBanner.msg}</span>
              <button onClick={() => setCallbackBanner(null)} className="text-xs underline opacity-70 hover:opacity-100">
                dismiss
              </button>
            </div>
          </div>
        )}

        {/* Connect CTA */}
        {!connected && !status.loading && (
          <div className="mb-8 rounded-lg border border-zinc-800 bg-zinc-900/40 p-6" data-testid="connect-section">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-medium">Step 1 — Connect your Restream account</h2>
                <p className="mt-1 text-sm text-zinc-400">
                  You'll be redirected to Restream to approve <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">clips.read</code> and{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">profile.default.read</code>.
                </p>
              </div>
              <Button onClick={handleConnect} disabled={connecting} testid="connect-restream-btn">
                {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
                Connect Restream
              </Button>
            </div>
          </div>
        )}

        {/* Main two-column layout */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* LEFT: projects + clips */}
          <section className="lg:col-span-3">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-lg font-medium">Clip projects</h2>
              {connected && (
                <button
                  onClick={fetchProjects}
                  className="text-xs text-zinc-400 underline-offset-2 hover:underline"
                  data-testid="refresh-projects-btn"
                >
                  refresh
                </button>
              )}
            </div>

            {!connected && (
              <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 p-8 text-center text-sm text-zinc-500">
                Connect Restream to see your clip projects.
              </div>
            )}

            {connected && projectsLoading && (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading projects…
              </div>
            )}

            {connected && projectsError && (
              <div className="rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/30" data-testid="projects-error">
                {projectsError}
              </div>
            )}

            {connected && !projectsLoading && !projectsError && projects.length === 0 && (
              <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 p-8 text-center text-sm text-zinc-500">
                No clip projects found. Create one at <a href="https://app.restream.io/clips" target="_blank" rel="noreferrer" className="underline">app.restream.io/clips</a>.
              </div>
            )}

            <ul className="space-y-2" data-testid="projects-list">
              {projects.map((p) => {
                const isOpen = !!expanded[p.id];
                const clips = clipsByProject[p.id];
                return (
                  <li key={p.id} className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/40">
                    <button
                      onClick={() => toggleProject(p.id)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-900"
                      data-testid={`project-${p.id}`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{p.title}</p>
                          <p className="text-xs text-zinc-500">
                            {p.clipCount || 0} clip{p.clipCount === 1 ? "" : "s"}
                            {p.createdAt && <> · {new Date(p.createdAt).toLocaleDateString()}</>}
                          </p>
                        </div>
                      </div>
                      <Badge tone="muted">{p.clipCount || 0}</Badge>
                    </button>

                    {isOpen && (
                      <div className="border-t border-zinc-800 bg-zinc-950/60 px-2 py-2">
                        {!clips && (
                          <div className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-500">
                            <Loader2 className="h-3 w-3 animate-spin" /> Loading clips…
                          </div>
                        )}
                        {clips?.error && (
                          <div className="m-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-200 ring-1 ring-red-500/30" data-testid={`clips-error-${p.id}`}>
                            <p className="font-medium">{clips.error.message || "Could not load clips."}</p>
                            {clips.error.upstream_status && (
                              <p className="mt-1 text-red-300/80">
                                (Restream API returned HTTP {clips.error.upstream_status}.) Try refresh.
                                If it keeps happening, disconnect and reconnect.
                              </p>
                            )}
                            {(clips.error.upstream_body || clips.error.upstream_url) && (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-red-300/80 hover:text-red-200">debug payload</summary>
                                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-black/40 p-2 text-[10px] text-red-200/80">
{JSON.stringify({ url: clips.error.upstream_url, body: clips.error.upstream_body }, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        )}
                        {Array.isArray(clips) && clips.length === 0 && (
                          <div className="px-3 py-2 text-xs text-zinc-500">No clips in this project.</div>
                        )}
                        {Array.isArray(clips) &&
                          clips.map((c) => (
                            <button
                              key={c.id}
                              onClick={() => selectClip(c, p)}
                              className={cn(
                                "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-zinc-800/60",
                                selectedClip?.id === c.id && "bg-amber-400/10 ring-1 ring-amber-400/30",
                              )}
                              data-testid={`clip-${c.id}`}
                            >
                              <span className="truncate text-zinc-200">{c.title}</span>
                              {c.durationSec && (
                                <span className="shrink-0 text-xs text-zinc-500">
                                  {Math.round(c.durationSec)}s
                                </span>
                              )}
                            </button>
                          ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {/* RIGHT: clip detail panel */}
          <aside className="lg:col-span-2">
            <div className="sticky top-6 rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid="clip-panel">
              <h2 className="mb-3 text-lg font-medium">Clip detail</h2>
              {!selectedClip ? (
                <p className="text-sm text-zinc-500">Select a clip on the left to inspect it.</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500">Title</p>
                    <p className="mt-1 text-sm text-zinc-100" data-testid="selected-clip-title">{selectedClip.title}</p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500">Transcript (first 500 chars)</p>
                    <p className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md bg-zinc-950 p-2 text-xs text-zinc-300 ring-1 ring-zinc-800">
                      {selectedClip.transcript ? selectedClip.transcript.slice(0, 500) : <span className="italic text-zinc-600">No transcript available.</span>}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={generateHashtags} disabled={aiLoading} variant="accent" testid="generate-ai-btn">
                      {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Generate Hashtags + Caption
                    </Button>
                    <Button onClick={getDownloadUrl} disabled={downloadLoading} variant="ghost" testid="download-url-btn">
                      {downloadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Get Download URL
                    </Button>
                  </div>

                  {aiError && (
                    <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300 ring-1 ring-red-500/30" data-testid="ai-error">
                      {aiError}
                    </div>
                  )}

                  {aiResult && (
                    <div className="space-y-3" data-testid="ai-result">
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <p className="text-xs uppercase tracking-wider text-zinc-500">Caption</p>
                          <button onClick={() => copy(aiResult.caption)} className="text-xs text-zinc-400 hover:text-zinc-200">
                            <Copy className="inline h-3 w-3" /> copy
                          </button>
                        </div>
                        <p className="rounded-md bg-zinc-950 p-2 text-sm text-zinc-200 ring-1 ring-zinc-800">{aiResult.caption}</p>
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <p className="text-xs uppercase tracking-wider text-zinc-500">Hashtags ({aiResult.hashtags?.length || 0})</p>
                          <button onClick={() => copy((aiResult.hashtags || []).join(" "))} className="text-xs text-zinc-400 hover:text-zinc-200">
                            <Copy className="inline h-3 w-3" /> copy all
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(aiResult.hashtags || []).map((h) => (
                            <span
                              key={h}
                              className={cn(
                                "rounded-full px-2 py-0.5 text-xs ring-1",
                                ["#truebeliever", "#truebelieverfam"].includes(h.toLowerCase())
                                  ? "bg-amber-400/15 text-amber-300 ring-amber-400/30"
                                  : "bg-zinc-800 text-zinc-200 ring-zinc-700",
                              )}
                            >
                              {h}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {downloadUrl && (
                    <div className="space-y-2" data-testid="download-area">
                      <p className="text-xs uppercase tracking-wider text-zinc-500">Temporary download URL</p>
                      <CodeBlock>{downloadUrl}</CodeBlock>
                      <video
                        key={downloadUrl}
                        controls
                        className="w-full overflow-hidden rounded-md bg-black ring-1 ring-zinc-800"
                        data-testid="clip-video"
                      >
                        <source src={downloadUrl} />
                      </video>
                    </div>
                  )}

                  <PostNowSection clip={selectedClip} caption={aiResult?.caption} hashtags={aiResult?.hashtags} />
                </div>
              )}
            </div>
          </aside>
        </div>

        <footer className="mt-12 border-t border-zinc-900 pt-6 text-xs text-zinc-600">
          Phase 0 POC · Backend: {BACKEND_URL || "(unset REACT_APP_BACKEND_URL)"} · <a href="/api/openapi.json" className="underline">OpenAPI</a>
        </footer>
      </div>
    </div>
  );
}

function PostNowSection({ clip, caption, hashtags }) {
  const [selected, setSelected] = useState({ youtube: true, facebook: true, instagram: true, tiktok: true });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const fullText = caption ? `${caption.trim()}\n\n${(hashtags || []).join(" ")}`.trim() : "";

  const platforms = [
    { key: "youtube", label: "YouTube", note: "Short" },
    { key: "facebook", label: "Facebook", note: "Page video" },
    { key: "instagram", label: "Instagram", note: "Reel" },
    { key: "tiktok", label: "TikTok", note: "drafts inbox" },
  ];

  const post = async () => {
    setBusy(true);
    setResults(null);
    const init = {};
    platforms.forEach(p => { if (selected[p.key]) init[p.key] = { status: "running" }; });
    setResults(init);
    try {
      const r = await axios.post(`${API}/post/now`, {
        clipId: clip.id, projectId: clip.projectId, clipTitle: clip.title || "",
        platforms: platforms.filter(p => selected[p.key]).map(p => p.key),
        caption: caption || "", hashtags: hashtags || [],
      }, { timeout: 600000 });
      setResults(r.data.results);
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e.message;
      const errResults = {};
      platforms.forEach(p => { if (selected[p.key]) errResults[p.key] = { status: "failed", error: errMsg }; });
      setResults(errResults);
    } finally {
      setBusy(false);
    }
  };

  const noContent = !caption || !hashtags?.length;

  return (
    <div className="space-y-3 rounded-md border border-amber-400/20 bg-amber-400/5 p-3" data-testid="post-now-section">
      <p className="text-xs uppercase tracking-wider text-amber-300">Post Now</p>
      {noContent ? (
        <p className="text-xs text-zinc-500">Generate caption + hashtags above first.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {platforms.map(p => (
              <label key={p.key} className="flex cursor-pointer items-center gap-2 rounded bg-zinc-950/60 px-2 py-1.5 text-xs ring-1 ring-zinc-800">
                <input
                  type="checkbox"
                  checked={selected[p.key]}
                  onChange={(e) => setSelected({ ...selected, [p.key]: e.target.checked })}
                  data-testid={`postnow-${p.key}-checkbox`}
                />
                <span className="text-zinc-200">{p.label}</span>
                <span className="text-zinc-500">· {p.note}</span>
              </label>
            ))}
          </div>
          <details className="rounded bg-zinc-950 p-2 text-[11px] text-zinc-300 ring-1 ring-zinc-800">
            <summary className="cursor-pointer text-zinc-400">Preview what gets posted</summary>
            <pre className="mt-2 whitespace-pre-wrap text-zinc-300">{fullText}</pre>
          </details>
          <button
            onClick={post}
            disabled={busy || !Object.values(selected).some(Boolean)}
            data-testid="post-now-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-amber-400 px-3 py-2 text-sm font-semibold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Post Now to Selected Platforms
          </button>
          {results && (
            <div className="space-y-1" data-testid="post-results">
              {Object.entries(results).map(([plat, res]) => (
                <div key={plat} className="rounded bg-zinc-950 px-2 py-1.5 text-xs ring-1 ring-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="capitalize text-zinc-300">{plat}</span>
                    {res.status === "running" && <Loader2 className="h-3 w-3 animate-spin text-amber-300" />}
                    {res.status === "success" && (
                      res.platform_url?.startsWith("http")
                        ? <a href={res.platform_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline">✓ view post</a>
                        : <span className="text-emerald-300">✓ {plat === "tiktok" ? "in TikTok inbox" : (res.platform_url || "posted")}</span>
                    )}
                    {res.status === "skipped" && <span className="text-zinc-500">↷ already posted</span>}
                    {res.status === "failed" && <span className="max-w-xs truncate text-red-300" title={res.error}>✗ {res.error}</span>}
                  </div>
                  {plat === "tiktok" && res.status === "success" && (
                    <TikTokCaptionHelper text={fullText} testid="postnow-tt-caption" />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
